// src/features/remoteAssist/components/VerificationCodeDialog.tsx

import { useState } from "react";
import { useConfirmRemoteAssistCode } from "../api";

interface VerificationCodeDialogProps {
  sessionId: string;
  role: "employee" | "supporter";
  /** روی کانال WebSocket، سرور کد را broadcast می‌کند — هر دو طرف باید همین را ببینند. */
  displayedCode?: string;
  onConfirmed?: () => void;
}

export function VerificationCodeDialog({ sessionId, role, displayedCode, onConfirmed }: VerificationCodeDialogProps) {
  const [inputCode, setInputCode] = useState("");
  const { mutate, isPending, error } = useConfirmRemoteAssistCode();

  const handleConfirm = () => {
    mutate(
      { sessionId, code: inputCode },
      { onSuccess: () => onConfirmed?.() },
    );
  };

  if (role === "employee") {
    return (
      <div
        dir="rtl"
        className="brand-card-extand"
        style={{
          maxWidth: "460px",
          margin: "2rem auto",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "var(--font-family-fa, sans-serif)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔐</div>
        <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.2rem", fontWeight: 800, color: "#007270" }}>
          کد تأیید اتصال امن
        </h2>
        <p style={{ margin: "0 0 1.5rem 0", fontSize: "0.9rem", color: "var(--color-text-muted, #666)", lineHeight: "1.6" }}>
          لطفاً کد ۶ رقمی زیر را برای پشتیبان بخوانید تا احراز هویت انجام شده و اتصال برقرار شود.
        </p>
        <div
          style={{
            padding: "16px",
            borderRadius: "14px",
            background: "rgba(0, 114, 112, 0.06)",
            border: "2px dashed #007270",
            fontSize: "2.5rem",
            fontWeight: 800,
            fontFamily: "monospace",
            letterSpacing: "12px",
            color: "#007270",
            direction: "ltr",
            marginBottom: "1.25rem",
          }}
        >
          {displayedCode || "------"}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "0.85rem", color: "#B45309" }}>
          <span>⏳</span>
          <span>در انتظار ورود و تایید کد توسط پشتیبان...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      className="brand-card-extand"
      style={{
        maxWidth: "460px",
        margin: "2rem auto",
        padding: "2rem",
        textAlign: "center",
        fontFamily: "var(--font-family-fa, sans-serif)",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
      }}
    >
      <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔑</div>
      <h2 style={{ margin: "0 0 0.5rem 0", fontSize: "1.2rem", fontWeight: 800, color: "#007270" }}>
        تأیید هویت کارمند
      </h2>
      <p style={{ margin: "0 0 1.5rem 0", fontSize: "0.9rem", color: "var(--color-text-muted, #666)", lineHeight: "1.6" }}>
        لطفاً کد ۶ رقمی اعلام‌شده توسط کارمند را در کادر زیر وارد کنید تا کنترل و راهنمایی صفحه آغاز شود.
      </p>
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={inputCode}
        onChange={(e) => setInputCode(e.target.value.replace(/\D/g, ""))}
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: "12px",
          border: "2px solid var(--color-border, #E4DCC8)",
          background: "#FFFFFF",
          fontSize: "2rem",
          fontWeight: 800,
          fontFamily: "monospace",
          letterSpacing: "10px",
          textAlign: "center",
          direction: "ltr",
          color: "#2B2B2B",
          outline: "none",
          marginBottom: "1.25rem",
        }}
        placeholder="------"
      />
      <button
        type="button"
        onClick={handleConfirm}
        disabled={inputCode.length !== 6 || isPending}
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: "10px",
          background: inputCode.length === 6 && !isPending ? "#007270" : "#E5E7EB",
          color: inputCode.length === 6 && !isPending ? "#FFFFFF" : "#9CA3AF",
          border: "none",
          fontSize: "1rem",
          fontWeight: 800,
          cursor: inputCode.length === 6 && !isPending ? "pointer" : "not-allowed",
          boxShadow: inputCode.length === 6 && !isPending ? "0 4px 12px rgba(0, 114, 112, 0.25)" : "none",
          transition: "all 0.2s ease",
        }}
      >
        {isPending ? "در حال تأیید..." : "تأیید و شروع سشن"}
      </button>
      {error && (
        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem 1rem",
            borderRadius: "8px",
            background: "rgba(239, 68, 68, 0.1)",
            color: "#DC2626",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          {/* @ts-ignore */}
          {error.response?.data?.detail || error.response?.data?.message || (error as Error).message || "کد واردشده نامعتبر است."}
        </div>
      )}
    </div>
  );
}

export default VerificationCodeDialog;
